import { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { RefreshCw } from 'lucide-react';
import { SelectDropdown } from '@/components/utility/select-dropdown/select-dropdown';
import { SkeletonLoader } from '@/components/utility/loaders/skeleton-loader/skeleton-loader';
import { getAnalytics } from '@/api/dashboard.api';
import {
  DateRange,
  type AnalyticsWidgetResponse,
  type DriverAnalyticsWidgetResponse,
  type StandardAnalyticsWidgetResponse,
} from '@/types/dashboard.types';
import { SurveyType } from '@/types/surveys.types';
import { useToast } from '@/contexts/toast-context';
import { TOAST_TYPES } from '@/constants/common.constants';
import { SURVEY_FILTER_VALUES } from '@/constants/survey.constants';
import { RouteSurveyScoreCard } from './route-survey-score-card';
import { SurveyCountCard } from './survey-count-card';
import { EnpsMetricCard } from './enps-metric-card';
import { AnalyticsWindowContext } from './analytics-window-context';
import './analytics-widget-style.scss';

interface AnalyticsWidgetProps {
  accountUuid?: string;
  userUuid?: string; // Used for fetching analytics data
  organizationLevelUuid?: string; // Used for navigation to surveys/organizations
  driverName?: string; // Used for navigation to surveys page
  isDriverView?: boolean; // Whether to render driver-specific metrics
  headerTitle?: string;
}

const DATE_RANGE_LABELS: Record<DateRange, string> = {
  [DateRange.ALL_DATA]: 'All Survey Dates',
  [DateRange.LAST_7_DAYS]: 'Last 7 days',
  [DateRange.LAST_30_DAYS]: 'Last 30 days',
};

export function AnalyticsWidget({
  accountUuid,
  userUuid,
  organizationLevelUuid,
  driverName,
  isDriverView = false,
  headerTitle = 'Analytics',
}: AnalyticsWidgetProps) {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [dateRange, setDateRange] = useState<DateRange>(DateRange.ALL_DATA);
  const [data, setData] = useState<AnalyticsWidgetResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const fetchAnalytics = async () => {
    try {
      setIsLoading(true);
      const response = await getAnalytics({
        dateRange,
        accountUuid,
        userUuid,
      });
      setData(response);
    } catch (error) {
      addToast('Failed to fetch analytics', {
        type: TOAST_TYPES.ERROR,
      });
      setData(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange, accountUuid, userUuid, refreshTrigger]);

  const handleRefresh = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleFilterChange = (event: { target: { value: string } }) => {
    setDateRange(event.target.value as DateRange);
  };

  // Map analytics date range to survey date range filter
  const mapDateRangeToSurveyFilter = (filter: DateRange): string => {
    switch (filter) {
      case DateRange.LAST_7_DAYS:
        return SURVEY_FILTER_VALUES.DATE_RANGE.LAST_7_DAYS;
      case DateRange.LAST_30_DAYS:
        return SURVEY_FILTER_VALUES.DATE_RANGE.LAST_30_DAYS;
      case DateRange.ALL_DATA:
      default:
        return SURVEY_FILTER_VALUES.DATE_RANGE.ALL;
    }
  };

  const navigateToSurveys = (filters: any) => {
    const search: any = {
      ...filters,
    };

    if (accountUuid) {
      search.accountUuid = accountUuid;
    }

    if (organizationLevelUuid) {
      search.organizationLevelUuid = organizationLevelUuid;
    }

    if (driverName) {
      search.searchText = driverName;
    }

    // Add date range filter based on current date range
    search.dateRange = mapDateRangeToSurveyFilter(dateRange);

    navigate({
      to: '/surveys',
      search,
    });
  };

  const renderDriverMetrics = () => {
    // Loading State
    if (!data) {
      return (
        <>
          <div className="metrics-section">
            <h3 className="metrics-section-title">Driver Metrics</h3>
            <div className="metrics-grid">
              <SkeletonLoader count={1} height={120} />
              <SkeletonLoader count={1} height={120} />
              <SkeletonLoader count={1} height={120} />
              <SkeletonLoader count={1} height={120} />
            </div>
          </div>
          <div className="metrics-section">
            <h3 className="metrics-section-title">Avg. Route Survey Scores</h3>
            <div className="metrics-grid">
              <SkeletonLoader count={1} height={120} />
              <SkeletonLoader count={1} height={120} />
              <SkeletonLoader count={1} height={120} />
              <SkeletonLoader count={1} height={120} />
            </div>
          </div>
        </>
      );
    }

    // Actual Data
    return (
      <>
        {/* Driver Metrics Group */}
        <div className="metrics-section">
          <h3 className="metrics-section-title">
            Driver Metrics{' '}
            <span className="metrics-section-date-range">
              ({DATE_RANGE_LABELS[dateRange]})
            </span>
          </h3>
          <div className="metrics-grid">
            <EnpsMetricCard
              title="Last eNPS Response"
              metric={
                (data as DriverAnalyticsWidgetResponse).enpsMetrics.lastEnps
              }
              dateRange={dateRange}
              isDriverDashboard={true}
              onNavigate={() =>
                navigateToSurveys({
                  surveyType: SurveyType.ROUTE_SURVEY,
                  sortBy: 'date',
                  direction: 'desc',
                })
              }
              tooltip="Click to see related surveys"
            />
            <SurveyCountCard
              title="Survey Comments"
              metric={data.surveyCounts.surveyComments}
              dateRange={dateRange}
              onNavigate={() =>
                navigateToSurveys({ showWithCommentsOnly: true })
              }
              tooltip="Click to see comments"
            />
            <SurveyCountCard
              title="Route Surveys"
              metric={data.surveyCounts.routeSurveys}
              dateRange={dateRange}
              onNavigate={() =>
                navigateToSurveys({ surveyType: SurveyType.ROUTE_SURVEY })
              }
              tooltip="Click to see surveys"
            />
            <SurveyCountCard
              title="Incident Reports"
              metric={data.surveyCounts.incidentReports}
              dateRange={dateRange}
              onNavigate={() =>
                navigateToSurveys({ surveyType: SurveyType.INCIDENT_REPORT })
              }
              tooltip="Click to see surveys"
            />
          </div>
        </div>

        {/* Survey Scores Group */}
        <div className="metrics-section">
          <h3 className="metrics-section-title">
            Avg. Route Survey Scores{' '}
            <span className="metrics-section-date-range">
              ({DATE_RANGE_LABELS[dateRange]})
            </span>
          </h3>
          <div className="metrics-grid metrics-grid-survey-scores">
            <div className="survey-scores-row">
              <RouteSurveyScoreCard
                title="Overall Score"
                metric={data.surveyScores.overallScore}
                dateRange={dateRange}
                onNavigate={() =>
                  navigateToSurveys({
                    surveyType: SurveyType.ROUTE_SURVEY,
                    scoreCategory: 'overall',
                    sortBy: 'overallScore',
                    direction: 'asc',
                  })
                }
                tooltip="Click to see related surveys"
              />
              <div
                className={`category-scores-merged${
                  data.surveyScores.categoryScores.length <= 2
                    ? ' category-scores-merged--few'
                    : ''
                }`}
              >
                {data.surveyScores.categoryScores.map((categoryScore) => (
                  <RouteSurveyScoreCard
                    key={categoryScore.category}
                    title={categoryScore.category}
                    metric={categoryScore.score}
                    dateRange={dateRange}
                    onNavigate={() =>
                      navigateToSurveys({
                        surveyType: SurveyType.ROUTE_SURVEY,
                        scoreCategory: categoryScore.category,
                        sortBy: 'categoryScore',
                        direction: 'asc',
                      })
                    }
                    tooltip="Click to see related surveys"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderStandardMetrics = () => {
    // Type-narrow to StandardAnalyticsWidgetResponse
    const standardData = data as StandardAnalyticsWidgetResponse;

    // Loading State
    if (!standardData) {
      return (
        <>
          <div className="metrics-section">
            <h3 className="metrics-section-title">Survey Counts</h3>
            <div className="metrics-grid">
              <SkeletonLoader count={1} height={120} />
              <SkeletonLoader count={1} height={120} />
              <SkeletonLoader count={1} height={120} />
            </div>
          </div>
          <div className="metrics-section">
            <h3 className="metrics-section-title">Driver eNPS Metrics</h3>
            <div className="metrics-grid">
              <SkeletonLoader count={1} height={120} />
              <SkeletonLoader count={1} height={120} />
              <SkeletonLoader count={1} height={120} />
              <SkeletonLoader count={1} height={120} />
            </div>
          </div>
          <div className="metrics-section">
            <h3 className="metrics-section-title">Avg. Route Survey Scores</h3>
            <div className="metrics-grid">
              <SkeletonLoader count={1} height={120} />
              <SkeletonLoader count={1} height={120} />
              <SkeletonLoader count={1} height={120} />
              <SkeletonLoader count={1} height={120} />
            </div>
          </div>
        </>
      );
    }

    // Actual Data
    return (
      <>
        {/* Survey Counts Group */}
        <div className="metrics-section">
          <h3 className="metrics-section-title">
            Survey Counts{' '}
            <span className="metrics-section-date-range">
              ({DATE_RANGE_LABELS[dateRange]})
            </span>
          </h3>
          <div className="metrics-grid">
            <SurveyCountCard
              title="Survey Comments"
              metric={standardData.surveyCounts.surveyComments}
              dateRange={dateRange}
              onNavigate={() =>
                navigateToSurveys({ showWithCommentsOnly: true })
              }
              tooltip="Click to see comments"
            />
            <SurveyCountCard
              title="Route Surveys"
              metric={standardData.surveyCounts.routeSurveys}
              dateRange={dateRange}
              onNavigate={() =>
                navigateToSurveys({ surveyType: SurveyType.ROUTE_SURVEY })
              }
              tooltip="Click to see surveys"
            />
            <SurveyCountCard
              title="Incident Reports"
              metric={standardData.surveyCounts.incidentReports}
              dateRange={dateRange}
              onNavigate={() =>
                navigateToSurveys({ surveyType: SurveyType.INCIDENT_REPORT })
              }
              tooltip="Click to see surveys"
            />
          </div>
        </div>

        {/* Driver eNPS Metrics Group */}
        <div className="metrics-section">
          <h3 className="metrics-section-title">
            Driver eNPS Metrics{' '}
            <span className="metrics-section-date-range">
              ({DATE_RANGE_LABELS[dateRange]})
            </span>
          </h3>
          <div className="enps-metrics-row">
            <EnpsMetricCard
              title="Driver eNPS"
              metric={standardData.enpsMetrics.enps}
              dateRange={dateRange}
              showTrendline={true}
              onNavigate={() =>
                navigateToSurveys({
                  surveyType: SurveyType.ROUTE_SURVEY,
                  sortBy: 'enpsScore',
                  direction: 'asc',
                })
              }
              tooltip="Click to see related surveys"
            />
            <div className="enps-secondary-metrics">
              <EnpsMetricCard
                title="Promoters"
                metric={standardData.enpsMetrics.promoters}
                dateRange={dateRange}
                onNavigate={() =>
                  navigateToSurveys({
                    surveyType: SurveyType.ROUTE_SURVEY,
                    sortBy: 'enpsScore',
                    direction: 'asc',
                  })
                }
                showPercentSymbol={true}
                tooltip="Click to see related surveys"
              />
              <EnpsMetricCard
                title="Passives"
                metric={standardData.enpsMetrics.passives}
                dateRange={dateRange}
                onNavigate={() =>
                  navigateToSurveys({
                    surveyType: SurveyType.ROUTE_SURVEY,
                    sortBy: 'enpsScore',
                    direction: 'asc',
                  })
                }
                showPercentSymbol={true}
                tooltip="Click to see related surveys"
              />
              <EnpsMetricCard
                title="Detractors"
                metric={standardData.enpsMetrics.detractors}
                dateRange={dateRange}
                onNavigate={() =>
                  navigateToSurveys({
                    surveyType: SurveyType.ROUTE_SURVEY,
                    sortBy: 'enpsScore',
                    direction: 'asc',
                  })
                }
                showPercentSymbol={true}
                tooltip="Click to see related surveys"
              />
            </div>
          </div>
        </div>

        {/* Survey Scores Group */}
        <div className="metrics-section">
          <h3 className="metrics-section-title">
            Avg. Route Survey Scores{' '}
            <span className="metrics-section-date-range">
              ({DATE_RANGE_LABELS[dateRange]})
            </span>
          </h3>
          <div className="metrics-grid metrics-grid-survey-scores">
            <div className="survey-scores-row">
              <RouteSurveyScoreCard
                title="Overall Score"
                metric={standardData.surveyScores.overallScore}
                dateRange={dateRange}
                onNavigate={() =>
                  navigateToSurveys({
                    surveyType: SurveyType.ROUTE_SURVEY,
                    scoreCategory: 'overall',
                    sortBy: 'overallScore',
                    direction: 'asc',
                  })
                }
                tooltip="Click to see related surveys"
              />
              <div
                className={`category-scores-merged${
                  standardData.surveyScores.categoryScores.length <= 2
                    ? ' category-scores-merged--few'
                    : ''
                }`}
              >
                {standardData.surveyScores.categoryScores.map(
                  (categoryScore) => (
                    <RouteSurveyScoreCard
                      key={categoryScore.category}
                      title={categoryScore.category}
                      metric={categoryScore.score}
                      dateRange={dateRange}
                      onNavigate={() =>
                        navigateToSurveys({
                          surveyType: SurveyType.ROUTE_SURVEY,
                          scoreCategory: categoryScore.category,
                          sortBy: 'categoryScore',
                          direction: 'asc',
                        })
                      }
                      tooltip="Click to see related surveys"
                    />
                  ),
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="dashboard-widget">
      <div className="dashboard-widget-header">
        <h1 className="dashboard-widget-title">{headerTitle}</h1>
        <div className="analytics-header-controls">
          <SelectDropdown
            id="date-range-filter"
            name="dateRange"
            value={dateRange}
            onChange={handleFilterChange}
            disabled={isLoading}
          >
            {Object.entries(DATE_RANGE_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </SelectDropdown>
          <button
            onClick={handleRefresh}
            className="analytics-refresh-button"
            aria-label="Refresh analytics data"
            disabled={isLoading}
          >
            <RefreshCw
              className={`analytics-refresh-icon ${
                isLoading ? 'spinning' : ''
              }`}
            />
          </button>
        </div>
      </div>
      <div>
        <AnalyticsWindowContext.Provider value={data?.window ?? null}>
          {isDriverView ? renderDriverMetrics() : renderStandardMetrics()}
        </AnalyticsWindowContext.Provider>
      </div>
    </div>
  );
}
